import { useState, useMemo, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CodingAPI, type CodingSurvey, type Codebook, type Matrix } from '../../api/coding';
import { Card, ErrorBanner, OkBanner } from '../Layout';
import { seedStarterMatrices } from '../../utils/starterMatrices';
import GenerateMatrixModal from './GenerateMatrixModal';

type Rule = {
  mode: 'preserve' | 'exclude' | 'range';
  reason: string;
  output_name?: string;
  breaks?: number[];
  labels?: string[];
};

interface SurveyPlanProps {
  survey: CodingSurvey;
  books: Codebook[];
  canCode?: boolean;
  onOpenMatrixEditor?: () => void;
  onOpenMatrixEditorWithSpec?: (name: string, spec: Matrix) => void;
  onGoToMatrices?: () => void;
}

function SampleResponsesDrawer({ surveyId, variable }: { surveyId: number; variable: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['coding-samples', surveyId, variable],
    queryFn: () => CodingAPI.sampleResponses(surveyId, variable, 20),
    staleTime: 60000,
  });

  if (isLoading) {
    return (
      <div className="p-3 bg-plano text-xs text-tinta-2 animate-pulse rounded border border-grid">
        Cargando respuestas reales desde ODK…
      </div>
    );
  }
  if (error) {
    return <ErrorBanner message={`No se pudieron cargar las respuestas: ${(error as Error).message}`} />;
  }
  if (!data?.items.length) {
    return (
      <div className="p-3 bg-plano text-xs text-tinta-3 rounded border border-grid">
        No se encontraron respuestas con texto no vacío para esta variable en los casos sincronizados de ODK.
      </div>
    );
  }

  return (
    <div className="p-3 bg-plano rounded border border-grid space-y-2 text-xs">
      <div className="flex justify-between items-center text-tinta-2 font-semibold pb-1 border-b border-grid">
        <span>Respuestas reales registradas en campo ({data.total} encontradas, mostrando muestra de {data.items.length})</span>
        <span className="text-[10px] text-tinta-3">Fuente: ODK Central</span>
      </div>
      <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
        {data.items.map((item, idx) => (
          <div key={item.case_id || idx} className="p-1.5 bg-superficie rounded border border-grid/60 flex items-start gap-2">
            <span className="font-mono text-[10px] text-tinta-3 whitespace-nowrap">#{item.case_id}</span>
            <p className="text-tinta break-words flex-1 font-sans">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SurveyPlan({
  survey,
  books,
  canCode = true,
  onOpenMatrixEditor,
  onOpenMatrixEditorWithSpec,
  onGoToMatrices,
}: SurveyPlanProps) {
  const qc = useQueryClient();
  const workstationRef = useRef<HTMLDivElement>(null);
  const [isIaModalOpen, setIsIaModalOpen] = useState(false);

  const dictionary = survey.dictionary || [];

  // Clasificadores de tipo de variable
  const isNumeric = (type?: string) => ['integer', 'decimal', 'numeric'].includes(type || '');
  const isOpenText = (type?: string) => ['text', 'string'].includes(type || '') || !type;
  const isClosed = (type?: string) =>
    ['select_one', 'select_multiple', 'select1', 'select'].some((t) => (type || '').startsWith(t));

  // Versiones de matrices publicadas
  const publishedVersions = useMemo(() => {
    return books.flatMap((b) =>
      b.versions
        .filter((v) => v.status === 'published')
        .map((v) => ({ book: b, version: v }))
    );
  }, [books]);

  // Selección activa de variable
  const [selectedVarName, setSelectedVarName] = useState<string>(() => {
    const firstOpen = dictionary.find((v) => isOpenText(v.type));
    return firstOpen ? firstOpen.name : dictionary[0]?.name || '';
  });

  // Filtros de navegación
  const [search, setSearch] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all');
  const [showSamples, setShowSamples] = useState(false);
  const [notice, setNotice] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [isSeeding, setIsSeeding] = useState(false);
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  // Variable activa actual
  const activeVar = dictionary.find((v) => v.name === selectedVarName) || dictionary[0];
  const vName = activeVar?.name || '';
  const vLabel = activeVar?.label || vName;
  const vType = activeVar?.type || 'text';

  const activeBinding = survey.bindings.find((b) => b.variable === vName);
  const plan = (survey.options.variable_plan as Record<string, Rule> | undefined)?.[vName];

  // Tratamiento activo en edición
  const initialTreatment: 'codificar' | 'preserve' | 'range' | 'exclude' = activeBinding
    ? 'codificar'
    : plan?.mode === 'exclude'
    ? 'exclude'
    : plan?.mode === 'range'
    ? 'range'
    : 'preserve';

  const [treatment, setTreatment] = useState<'codificar' | 'preserve' | 'range' | 'exclude'>(initialTreatment);

  const defaultOutputName = useMemo(() => {
    return vName.split('/').pop()?.replace(/[^A-Za-z0-9_]/g, '_') ?? 'var_cod';
  }, [vName]);

  const [targetVersion, setTargetVersion] = useState<string>('');
  const [outputName, setOutputName] = useState<string>('');
  const [rangeName, setRangeName] = useState('');
  const [rangeBreaks, setRangeBreaks] = useState('18, 30, 50, 65');
  const [rangeLabels, setRangeLabels] = useState('18 a 29 años\n30 a 49 años\n50 a 64 años\n65 años o más');
  const [excludeReason, setExcludeReason] = useState('');

  // Sincronizar formulario al cambiar de variable seleccionada o datos de encuesta
  useEffect(() => {
    if (!activeVar) return;
    const b = survey.bindings.find((x) => x.variable === activeVar.name);
    const p = (survey.options.variable_plan as Record<string, Rule> | undefined)?.[activeVar.name];

    const currentMode = b ? 'codificar' : p?.mode === 'exclude' ? 'exclude' : p?.mode === 'range' ? 'range' : 'preserve';
    setTreatment(currentMode);

    const defOut = activeVar.name.split('/').pop()?.replace(/[^A-Za-z0-9_]/g, '_') ?? 'var_cod';
    setOutputName(b?.output_name || defOut);
    setTargetVersion(b ? String(b.version_id) : publishedVersions[0] ? String(publishedVersions[0].version.id) : '');

    setRangeName(p?.output_name || `${defOut}_rango`);
    setRangeBreaks(p?.breaks?.join(', ') || '18, 30, 50, 65');
    setRangeLabels(p?.labels?.join('\n') || '18 a 29 años\n30 a 49 años\n50 a 64 años\n65 años o más');
    setExcludeReason(p?.reason || '');
    setShowSamples(false);
  }, [activeVar?.name, survey.bindings, survey.options.variable_plan, publishedVersions]);

  // Contadores
  const boundCount = survey.bindings.length;
  const rangeCount = Object.values(survey.options.variable_plan || {}).filter(
    (p: any) => p?.mode === 'range'
  ).length;
  const excludeCount = Object.values(survey.options.variable_plan || {}).filter(
    (p: any) => p?.mode === 'exclude'
  ).length;
  const preserveCount = dictionary.length - boundCount - rangeCount - excludeCount;
  const closedCount = dictionary.filter((v) => isClosed(v.type)).length;

  // Filtrado de variables
  const filteredVariables = useMemo(() => {
    return dictionary.filter((v) => {
      if (search.trim()) {
        const q = search.toLowerCase();
        const mName = v.name.toLowerCase().includes(q);
        const mLabel = (v.label || '').toLowerCase().includes(q);
        if (!mName && !mLabel) return false;
      }
      if (filterType === 'bound') return survey.bindings.some((b) => b.variable === v.name);
      if (filterType === 'range') {
        const p = (survey.options.variable_plan as Record<string, Rule> | undefined)?.[v.name];
        return p?.mode === 'range';
      }
      if (filterType === 'exclude') {
        const p = (survey.options.variable_plan as Record<string, Rule> | undefined)?.[v.name];
        return p?.mode === 'exclude';
      }
      if (filterType === 'open') return isOpenText(v.type);
      if (filterType === 'numeric') return isNumeric(v.type);
      if (filterType === 'closed') return isClosed(v.type);
      if (filterType === 'unassigned') {
        const isBound = survey.bindings.some((b) => b.variable === v.name);
        const p = (survey.options.variable_plan as Record<string, Rule> | undefined)?.[v.name];
        return !isBound && !p;
      }
      return true;
    });
  }, [dictionary, search, filterType, survey.bindings, survey.options.variable_plan]);

  const currentIndex = filteredVariables.findIndex((v) => v.name === vName);

  // Navegación anterior / siguiente
  const handlePrev = () => {
    if (currentIndex > 0) {
      setSelectedVarName(filteredVariables[currentIndex - 1].name);
      setNotice(null);
    }
  };

  const handleNext = () => {
    if (currentIndex < filteredVariables.length - 1) {
      setSelectedVarName(filteredVariables[currentIndex + 1].name);
      setNotice(null);
    }
  };

  // Mutación general
  const actionMutation = useMutation({
    mutationFn: async (job: () => Promise<unknown>) => job(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['coding-surveys'] });
      void qc.invalidateQueries({ queryKey: ['coding-books'] });
    },
    onError: (err: Error) => {
      setNotice({ type: 'err', text: err.message || 'Error al guardar los cambios.' });
    },
  });

  const isBusy = actionMutation.isPending || isSeeding || isBulkSaving;

  // Guardar tratamiento de la variable activa
  const handleSaveTreatment = async (advanceNext = false) => {
    setNotice(null);
    if (!vName) return;

    try {
      if (treatment === 'codificar') {
        if (!targetVersion) {
          setNotice({ type: 'err', text: 'Seleccione una matriz de códigos publicada para vincular.' });
          return;
        }
        if (!outputName.trim()) {
          setNotice({ type: 'err', text: 'Especifique el nombre de la variable codificada resultante.' });
          return;
        }
        await CodingAPI.bind(survey.id, vName, Number(targetVersion), outputName.trim());
        setNotice({ type: 'ok', text: `Variable «${vName}» vinculada a matriz de códigos con éxito.` });
      } else {
        // Si estaba vinculada previamente a una matriz, desvincularla para no dejar estado inconsistente
        if (activeBinding) {
          await CodingAPI.unbind(survey.id, activeBinding.id);
        }

        if (treatment === 'preserve') {
          await CodingAPI.configure(survey.id, {
            options: {
              variable_plan: {
                [vName]: { mode: 'preserve', reason: '' },
              },
            },
          });
          setNotice({ type: 'ok', text: `«${vName}» configurada para conservar valores originales.` });
        } else if (treatment === 'range') {
          const parsedBreaks = rangeBreaks
            .split(',')
            .map((x) => Number(x.trim()))
            .filter((x) => !isNaN(x));
          const parsedLabels = rangeLabels.split('\n').filter((x) => x.trim().length > 0);
          if (parsedBreaks.length < 2) {
            setNotice({ type: 'err', text: 'Debe especificar al menos 2 límites numéricos separados por comas.' });
            return;
          }
          if (parsedLabels.length !== parsedBreaks.length - 1) {
            setNotice({
              type: 'err',
              text: `Para ${parsedBreaks.length} límites necesita exactamente ${parsedBreaks.length - 1} etiquetas (actualmente tiene ${parsedLabels.length}).`,
            });
            return;
          }
          await CodingAPI.configure(survey.id, {
            options: {
              variable_plan: {
                [vName]: {
                  mode: 'range',
                  reason: '',
                  output_name: rangeName.trim() || `${defaultOutputName}_rango`,
                  breaks: parsedBreaks,
                  labels: parsedLabels,
                },
              },
            },
          });
          setNotice({ type: 'ok', text: `Rangos numéricos guardados para «${vName}».` });
        } else if (treatment === 'exclude') {
          if (!excludeReason.trim()) {
            setNotice({ type: 'err', text: 'Debe indicar un motivo o justificación técnica para excluir esta variable.' });
            return;
          }
          await CodingAPI.configure(survey.id, {
            options: {
              variable_plan: {
                [vName]: { mode: 'exclude', reason: excludeReason.trim() },
              },
            },
          });
          setNotice({ type: 'ok', text: `Variable «${vName}» excluida del análisis con justificación.` });
        }
      }

      await qc.invalidateQueries({ queryKey: ['coding-surveys'] });

      if (advanceNext && currentIndex < filteredVariables.length - 1) {
        setSelectedVarName(filteredVariables[currentIndex + 1].name);
      }
    } catch (e) {
      setNotice({ type: 'err', text: (e as Error).message || 'Error al guardar el tratamiento.' });
    }
  };

  // Desvincular manualmente
  const handleUnbind = async () => {
    if (!activeBinding) return;
    setNotice(null);
    try {
      await CodingAPI.unbind(survey.id, activeBinding.id);
      setTreatment('preserve');
      setNotice({ type: 'ok', text: `Variable «${vName}» desvinculada de la matriz.` });
      await qc.invalidateQueries({ queryKey: ['coding-surveys'] });
    } catch (e) {
      setNotice({ type: 'err', text: (e as Error).message || 'Error al desvincular.' });
    }
  };

  // Conservar masivamente preguntas cerradas
  const handleBulkPreserveClosed = async () => {
    setIsBulkSaving(true);
    setNotice(null);
    try {
      const closedVars = dictionary.filter((v) => isClosed(v.type));
      const bulkPlan: Record<string, Rule> = {};
      for (const v of closedVars) {
        // No sobreescribir si ya tiene matriz vinculada
        if (!survey.bindings.some((b) => b.variable === v.name)) {
          bulkPlan[v.name] = { mode: 'preserve', reason: '' };
        }
      }
      await CodingAPI.configure(survey.id, {
        options: {
          variable_plan: bulkPlan,
        },
      });
      setNotice({
        type: 'ok',
        text: `Se aplicó «Conservar original» a ${Object.keys(bulkPlan).length} preguntas cerradas y demográficas.`,
      });
      await qc.invalidateQueries({ queryKey: ['coding-surveys'] });
    } catch (e) {
      setNotice({ type: 'err', text: (e as Error).message || 'Error en guardado masivo.' });
    } finally {
      setIsBulkSaving(false);
    }
  };

  // Generar matrices recomendadas
  const handleSeedStarterMatrices = async () => {
    setIsSeeding(true);
    setNotice(null);
    try {
      const res = await seedStarterMatrices(survey.project_id);
      await qc.invalidateQueries({ queryKey: ['coding-books'] });
      setNotice({
        type: 'ok',
        text: `Matrices generadas con éxito: ${res.published} publicadas listas para vincular a sus preguntas abiertas.`,
      });
    } catch (e) {
      setNotice({ type: 'err', text: (e as Error).message || 'Error al generar matrices recomendadas.' });
    } finally {
      setIsSeeding(false);
    }
  };

  // Función para obtener insignia visual de estado
  const renderBadge = (varName: string) => {
    const b = survey.bindings.find((x) => x.variable === varName);
    const p = (survey.options.variable_plan as Record<string, Rule> | undefined)?.[varName];

    if (b) {
      const bookVersion = publishedVersions.find((pv) => pv.version.id === b.version_id);
      const bookName = bookVersion ? bookVersion.book.name : `Matriz #${b.version_id}`;
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium bg-estado-exito/15 text-estado-exito break-words">
          <span>●</span> Codificada: <strong>{bookName}</strong> → <span className="font-mono">{b.output_name}</span>
        </span>
      );
    }
    if (p?.mode === 'range') {
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium bg-marca/15 text-marca break-words">
          <span>🔢</span> Rangos: <span className="font-mono">{p.output_name}</span>
        </span>
      );
    }
    if (p?.mode === 'exclude') {
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium bg-plano text-tinta-3 border border-grid">
          <span>🚫</span> Excluida
        </span>
      );
    }
    if (p?.mode === 'preserve') {
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium bg-plano text-tinta-2">
          <span>📋</span> Valores originales
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium bg-plano text-tinta-3">
        <span>○</span> Sin configurar (Original)
      </span>
    );
  };

  return (
    <div className="space-y-4 min-w-0" ref={workstationRef}>
      {/* 1. Encabezado y resumen de estado */}
      <Card>
        <div className="space-y-4 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-grid pb-3">
            <div>
              <h2 className="text-lg font-semibold text-tinta">Tratamiento y plan de variables</h2>
              <p className="text-xs text-tinta-3 mt-0.5">
                Defina qué hacer con cada pregunta de la encuesta: codificar respuestas abiertas con matrices, conservar originales, crear rangos o excluirlas.
              </p>
            </div>
            {closedCount > 0 && canCode && (
              <button
                type="button"
                className="btn-sec text-xs min-h-[44px]"
                disabled={isBusy}
                onClick={handleBulkPreserveClosed}
                title="Marca de una sola vez todas las preguntas cerradas para conservar sus valores originales"
              >
                {isBulkSaving ? 'Aplicando…' : `⚡ Conservar originales en cerradas (${closedCount})`}
              </button>
            )}
          </div>

          {/* Estadísticas de cobertura */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            <div className="p-2.5 rounded bg-plano border border-grid">
              <span className="text-tinta-3 block text-[11px]">Total variables</span>
              <span className="font-bold text-sm text-tinta">{dictionary.length}</span>
            </div>
            <div className="p-2.5 rounded bg-estado-exito/10 border border-estado-exito/20">
              <span className="text-estado-exito block text-[11px]">Codificando con matriz</span>
              <span className="font-bold text-sm text-estado-exito">{boundCount}</span>
            </div>
            <div className="p-2.5 rounded bg-marca/10 border border-marca/20">
              <span className="text-marca block text-[11px]">Rangos numéricos</span>
              <span className="font-bold text-sm text-marca">{rangeCount}</span>
            </div>
            <div className="p-2.5 rounded bg-plano border border-grid">
              <span className="text-tinta-3 block text-[11px]">Excluidas</span>
              <span className="font-bold text-sm text-tinta-3">{excludeCount}</span>
            </div>
            <div className="p-2.5 rounded bg-plano border border-grid">
              <span className="text-tinta-2 block text-[11px]">Valores originales</span>
              <span className="font-bold text-sm text-tinta-2">{preserveCount}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* 2. Ficha de Trabajo de la Pregunta Seleccionada */}
      {activeVar && (
        <Card>
          <div className="space-y-4 min-w-0">
            {/* Barra de navegación de pregunta */}
            <div className="space-y-2 bg-plano p-3 rounded-lg border border-grid">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-semibold text-tinta">
                  Configurar pregunta {currentIndex >= 0 ? `${currentIndex + 1} de ${filteredVariables.length}` : ''}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    className="btn-sec text-xs min-h-[44px] px-3"
                    disabled={currentIndex <= 0}
                    onClick={handlePrev}
                  >
                    ← Anterior
                  </button>
                  <button
                    type="button"
                    className="btn-sec text-xs min-h-[44px] px-3"
                    disabled={currentIndex >= filteredVariables.length - 1}
                    onClick={handleNext}
                  >
                    Siguiente →
                  </button>
                </div>
              </div>

              {/* Selector desplegable de variable */}
              <label className="block">
                <span className="text-[11px] text-tinta-3 block mb-1">Seleccionar pregunta a configurar:</span>
                <select
                  aria-label="Seleccionar pregunta a configurar"
                  className="input w-full text-xs sm:text-sm font-medium min-h-[44px] cursor-pointer"
                  value={selectedVarName}
                  onChange={(e) => {
                    setSelectedVarName(e.target.value);
                    setNotice(null);
                  }}
                >
                  {filteredVariables.map((v, i) => {
                    const b = survey.bindings.find((x) => x.variable === v.name);
                    const p = (survey.options.variable_plan as Record<string, Rule> | undefined)?.[v.name];
                    const tag = b ? ' [● Codificada]' : p?.mode === 'range' ? ' [🔢 Rangos]' : p?.mode === 'exclude' ? ' [🚫 Excluida]' : '';
                    return (
                      <option key={v.name} value={v.name}>
                        #{i + 1} · {v.name} {v.label && v.label !== v.name ? `— ${v.label.slice(0, 45)}` : ''} ({v.type || 'text'}){tag}
                      </option>
                    );
                  })}
                </select>
              </label>
            </div>

            {/* Ficha técnica de la pregunta */}
            <div className="p-3.5 bg-superficie rounded-lg border border-grid space-y-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                  <span className="font-mono text-xs font-bold text-tinta px-2 py-0.5 rounded bg-plano border border-grid">
                    {vName}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-plano border border-grid text-tinta-3">
                    Tipo: {vType}
                  </span>
                </div>
                {renderBadge(vName)}
              </div>

              <div className="pt-1">
                <h3 className="text-base font-semibold text-tinta break-words">
                  {vLabel}
                </h3>
              </div>

              <div className="flex flex-wrap gap-2 pt-1 border-t border-grid/60">
                <button
                  type="button"
                  className="btn-sec text-xs min-h-[44px] px-3"
                  onClick={() => setShowSamples(!showSamples)}
                >
                  {showSamples ? '✕ Ocultar respuestas de campo' : '👁 Ver respuestas reales en ODK'}
                </button>
              </div>

              {/* Cajón de respuestas de muestra */}
              {showSamples && <SampleResponsesDrawer surveyId={survey.id} variable={vName} />}
            </div>

            {/* Notificaciones */}
            {notice?.type === 'err' && <ErrorBanner message={notice.text} />}
            {notice?.type === 'ok' && <OkBanner message={notice.text} />}

            {/* Opciones de Tratamiento: 4 botones táctiles grandes */}
            <div>
              <label className="block text-xs font-semibold text-tinta mb-2">
                Seleccione el tratamiento para «{vName}»:
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                <button
                  type="button"
                  className={`p-3 rounded-lg border text-left flex flex-col justify-between gap-1 transition-all min-h-[56px] ${
                    treatment === 'preserve'
                      ? 'border-marca bg-marca/10 text-marca font-semibold shadow-sm'
                      : 'border-grid hover:bg-plano text-tinta'
                  }`}
                  onClick={() => setTreatment('preserve')}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-base">📋</span>
                    <span className="text-xs font-bold">Conservar original</span>
                  </div>
                  <span className="text-[11px] opacity-75">Mantener valores de ODK sin cambios</span>
                </button>

                <button
                  type="button"
                  className={`p-3 rounded-lg border text-left flex flex-col justify-between gap-1 transition-all min-h-[56px] ${
                    treatment === 'codificar'
                      ? 'border-marca bg-marca/10 text-marca font-semibold shadow-sm'
                      : 'border-grid hover:bg-plano text-tinta'
                  }`}
                  onClick={() => setTreatment('codificar')}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-base">🏷️</span>
                    <span className="text-xs font-bold">Codificar con matriz</span>
                  </div>
                  <span className="text-[11px] opacity-75">Clasificar respuestas con matriz de códigos</span>
                </button>

                <button
                  type="button"
                  className={`p-3 rounded-lg border text-left flex flex-col justify-between gap-1 transition-all min-h-[56px] ${
                    treatment === 'range'
                      ? 'border-marca bg-marca/10 text-marca font-semibold shadow-sm'
                      : 'border-grid hover:bg-plano text-tinta'
                  }`}
                  onClick={() => setTreatment('range')}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-base">🔢</span>
                    <span className="text-xs font-bold">Rangos numéricos</span>
                  </div>
                  <span className="text-[11px] opacity-75">Agrupar edades o valores en intervalos</span>
                </button>

                <button
                  type="button"
                  className={`p-3 rounded-lg border text-left flex flex-col justify-between gap-1 transition-all min-h-[56px] ${
                    treatment === 'exclude'
                      ? 'border-marca bg-marca/10 text-marca font-semibold shadow-sm'
                      : 'border-grid hover:bg-plano text-tinta'
                  }`}
                  onClick={() => setTreatment('exclude')}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-base">🚫</span>
                    <span className="text-xs font-bold">Excluir del análisis</span>
                  </div>
                  <span className="text-[11px] opacity-75">Omitir de informes con justificación</span>
                </button>
              </div>
            </div>

            {/* Formulario específico según tratamiento seleccionado */}
            <div className="p-4 bg-plano rounded-lg border border-grid space-y-3">
              {treatment === 'preserve' && (
                <div className="space-y-2 text-xs">
                  <h4 className="font-semibold text-tinta">Conservar valores originales</h4>
                  <p className="text-tinta-2">
                    Las respuestas de esta variable se incluirán en los informes y bases exportadas exactamente como fueron capturadas en campo. Recomendado para preguntas cerradas, opciones múltiples y datos demográficos ya normalizados.
                  </p>
                </div>
              )}

              {treatment === 'codificar' && (
                <div className="space-y-3 text-xs">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-grid pb-2">
                    <h4 className="font-semibold text-tinta text-sm">Vincular a matriz de códigos</h4>
                    <button
                      type="button"
                      className="btn text-xs min-h-[38px] px-3 font-semibold"
                      onClick={() => setIsIaModalOpen(true)}
                    >
                      🪄 Generar matriz con IA para «{activeVar?.name}»
                    </button>
                  </div>

                  {publishedVersions.length === 0 ? (
                    <div className="p-3 bg-superficie rounded border border-grid space-y-2">
                      <p className="font-medium text-tinta">
                        No hay matrices publicadas en este proyecto todavía.
                      </p>
                      <p className="text-tinta-2">
                        Para codificar esta pregunta, puede generar las matrices recomendadas con un solo clic o crear una nueva matriz en la biblioteca.
                      </p>
                      <div className="flex flex-wrap gap-2 pt-1">
                        <button
                          type="button"
                          className="btn text-xs min-h-[44px]"
                          disabled={isBusy}
                          onClick={handleSeedStarterMatrices}
                        >
                          {isSeeding ? 'Generando matrices…' : '🪄 Crear matrices recomendadas para esta encuesta'}
                        </button>
                        {onOpenMatrixEditor && (
                          <button
                            type="button"
                            className="btn-sec text-xs min-h-[44px]"
                            onClick={onOpenMatrixEditor}
                          >
                            + Crear matriz manual
                          </button>
                        )}
                        {onGoToMatrices && (
                          <button
                            type="button"
                            className="btn-sec text-xs min-h-[44px]"
                            onClick={onGoToMatrices}
                          >
                            Ir a Biblioteca de matrices →
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <label className="etiqueta">
                        Matriz de codificación publicada:
                        <select
                          className="input min-h-[44px]"
                          value={targetVersion}
                          onChange={(e) => setTargetVersion(e.target.value)}
                        >
                          <option value="">Seleccione una matriz publicada…</option>
                          {publishedVersions.map((pv) => (
                            <option key={pv.version.id} value={pv.version.id}>
                              {pv.book.name} (v{pv.version.number} · {pv.version.specification.codes.length} categorías)
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="etiqueta">
                        Nombre de la variable codificada resultante en la base:
                        <input
                          className="input font-mono min-h-[44px]"
                          value={outputName}
                          onChange={(e) => setOutputName(e.target.value)}
                          placeholder="ej. razon_voto1_cod"
                          required
                        />
                        <span className="text-[11px] text-tinta-3 mt-1 block">
                          Generará las columnas: <span className="font-mono">{outputName}_codigo</span>, <span className="font-mono">{outputName}_estado_respuesta</span>, etc.
                        </span>
                      </label>

                      {activeBinding && (
                        <div className="p-2.5 bg-superficie rounded border border-grid flex flex-wrap items-center justify-between gap-2">
                          <span className="text-tinta-2">
                            Esta variable ya se encuentra vinculada a la matriz #{activeBinding.version_id}.
                          </span>
                          <button
                            type="button"
                            className="btn-sec text-xs text-estado-critico min-h-[44px] px-3"
                            disabled={isBusy}
                            onClick={handleUnbind}
                          >
                            Desvincular variable
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {treatment === 'range' && (
                <div className="space-y-3 text-xs">
                  <h4 className="font-semibold text-tinta">Crear rangos numéricos</h4>

                  <label className="etiqueta">
                    Nombre de la variable resultante con rangos:
                    <input
                      className="input font-mono min-h-[44px]"
                      value={rangeName}
                      onChange={(e) => setRangeName(e.target.value)}
                      placeholder="ej. edad_rango"
                      required
                    />
                  </label>

                  <label className="etiqueta">
                    Límites numéricos separados por comas:
                    <input
                      className="input min-h-[44px]"
                      value={rangeBreaks}
                      onChange={(e) => setRangeBreaks(e.target.value)}
                      placeholder="18, 30, 50, 65"
                      required
                    />
                    <span className="text-[11px] text-tinta-3 mt-1 block">
                      Cada intervalo incluye el límite inferior y excluye el superior; el último intervalo incluye ambos.
                    </span>
                  </label>

                  <label className="etiqueta">
                    Una etiqueta por cada intervalo (una línea por intervalo):
                    <textarea
                      className="input font-sans text-xs"
                      rows={4}
                      value={rangeLabels}
                      onChange={(e) => setRangeLabels(e.target.value)}
                      placeholder={'18 a 29 años\n30 a 49 años\n50 a 64 años\n65 años o más'}
                      required
                    />
                  </label>
                </div>
              )}

              {treatment === 'exclude' && (
                <div className="space-y-3 text-xs">
                  <h4 className="font-semibold text-tinta">Excluir variable del análisis</h4>
                  <p className="text-tinta-2">
                    La variable no se incluirá en cruces ni informes. Toda exclusión debe registrar una justificación metodológica.
                  </p>

                  <label className="etiqueta">
                    Motivo obligatorio de la exclusión:
                    <textarea
                      className="input text-xs"
                      rows={2}
                      value={excludeReason}
                      onChange={(e) => setExcludeReason(e.target.value)}
                      placeholder="ej. Variable técnica de control del encuestador sin valor analítico para el cliente"
                      required
                    />
                  </label>
                </div>
              )}

              {/* Botones principales de acción con tamaño táctil adecuado */}
              {canCode && (
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-grid">
                  <button
                    type="button"
                    className="btn text-xs min-h-[44px] px-4"
                    disabled={isBusy || (treatment === 'codificar' && publishedVersions.length === 0)}
                    onClick={() => handleSaveTreatment(false)}
                  >
                    {actionMutation.isPending ? 'Guardando…' : 'Guardar tratamiento'}
                  </button>

                  <button
                    type="button"
                    className="btn-sec text-xs min-h-[44px] px-4"
                    disabled={isBusy || (treatment === 'codificar' && publishedVersions.length === 0) || currentIndex >= filteredVariables.length - 1}
                    onClick={() => handleSaveTreatment(true)}
                  >
                    Guardar y siguiente pregunta →
                  </button>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* 3. Tabla de Cobertura Completa de Toda la Encuesta */}
      <Card>
        <div className="space-y-3 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-base font-semibold text-tinta">Cobertura total de la encuesta ({dictionary.length} variables)</h3>
              <p className="text-xs text-tinta-3">
                Revise el estado asignado a cada pregunta. Haga clic en «Configurar» para editarla en la ficha superior.
              </p>
            </div>
          </div>

          {/* Búsqueda y filtros rápidos */}
          <div className="flex flex-wrap gap-2 items-center justify-between pt-1">
            <div className="flex-1 min-w-0">
              <input
                type="text"
                className="input text-xs py-1.5 w-full min-h-[44px]"
                placeholder="Buscar por nombre técnico o texto de la pregunta…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="flex flex-wrap gap-1 text-xs">
              <button
                type="button"
                className={`px-2.5 py-1.5 rounded transition-colors min-h-[36px] ${
                  filterType === 'all' ? 'bg-marca text-blanco font-semibold' : 'bg-plano text-tinta-2 hover:bg-plano/80'
                }`}
                onClick={() => setFilterType('all')}
              >
                Todas ({dictionary.length})
              </button>
              <button
                type="button"
                className={`px-2.5 py-1.5 rounded transition-colors min-h-[36px] ${
                  filterType === 'open' ? 'bg-marca text-blanco font-semibold' : 'bg-plano text-tinta-2 hover:bg-plano/80'
                }`}
                onClick={() => setFilterType('open')}
              >
                Abiertas ({dictionary.filter((v) => isOpenText(v.type)).length})
              </button>
              <button
                type="button"
                className={`px-2.5 py-1.5 rounded transition-colors min-h-[36px] ${
                  filterType === 'bound' ? 'bg-marca text-blanco font-semibold' : 'bg-plano text-tinta-2 hover:bg-plano/80'
                }`}
                onClick={() => setFilterType('bound')}
              >
                Codificadas ({boundCount})
              </button>
              <button
                type="button"
                className={`px-2.5 py-1.5 rounded transition-colors min-h-[36px] ${
                  filterType === 'closed' ? 'bg-marca text-blanco font-semibold' : 'bg-plano text-tinta-2 hover:bg-plano/80'
                }`}
                onClick={() => setFilterType('closed')}
              >
                Cerradas ({closedCount})
              </button>
              <button
                type="button"
                className={`px-2.5 py-1.5 rounded transition-colors min-h-[36px] ${
                  filterType === 'numeric' ? 'bg-marca text-blanco font-semibold' : 'bg-plano text-tinta-2 hover:bg-plano/80'
                }`}
                onClick={() => setFilterType('numeric')}
              >
                Numéricas ({dictionary.filter((v) => isNumeric(v.type)).length})
              </button>
              <button
                type="button"
                className={`px-2.5 py-1.5 rounded transition-colors min-h-[36px] ${
                  filterType === 'range' ? 'bg-marca text-blanco font-semibold' : 'bg-plano text-tinta-2 hover:bg-plano/80'
                }`}
                onClick={() => setFilterType('range')}
              >
                Con rangos ({rangeCount})
              </button>
              <button
                type="button"
                className={`px-2.5 py-1.5 rounded transition-colors min-h-[36px] ${
                  filterType === 'exclude' ? 'bg-marca text-blanco font-semibold' : 'bg-plano text-tinta-2 hover:bg-plano/80'
                }`}
                onClick={() => setFilterType('exclude')}
              >
                Excluidas ({excludeCount})
              </button>
            </div>
          </div>

          {/* Tabla responsiva */}
          <div className="overflow-x-auto min-w-0 border border-grid rounded-md">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-plano border-b border-grid text-tinta-3">
                  <th className="p-2.5 font-medium">#</th>
                  <th className="p-2.5 font-medium">Variable técnica</th>
                  <th className="p-2.5 font-medium">Pregunta / Etiqueta</th>
                  <th className="p-2.5 font-medium">Tipo</th>
                  <th className="p-2.5 font-medium">Tratamiento actual</th>
                  <th className="p-2.5 font-medium text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-grid">
                {filteredVariables.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-tinta-3">
                      No se encontraron variables con el filtro actual.
                    </td>
                  </tr>
                ) : (
                  filteredVariables.map((v, idx) => {
                    const isSelected = v.name === vName;
                    return (
                      <tr
                        key={v.name}
                        className={`transition-colors ${
                          isSelected ? 'bg-marca/5 font-medium' : 'hover:bg-plano/50'
                        }`}
                      >
                        <td className="p-2.5 text-tinta-3 font-mono">{idx + 1}</td>
                        <td className="p-2.5 font-mono text-tinta font-semibold break-words max-w-[160px]">
                          {v.name}
                        </td>
                        <td className="p-2.5 text-tinta-2 break-words max-w-[260px]">
                          {v.label || v.name}
                        </td>
                        <td className="p-2.5 text-tinta-3 whitespace-nowrap">
                          <span className="px-1.5 py-0.5 rounded bg-plano border border-grid text-[11px]">
                            {v.type || 'text'}
                          </span>
                        </td>
                        <td className="p-2.5 min-w-0">
                          {renderBadge(v.name)}
                        </td>
                        <td className="p-2.5 text-right whitespace-nowrap">
                          <button
                            type="button"
                            className={`btn-sec text-xs min-h-[36px] px-2.5 ${
                              isSelected ? 'border-marca text-marca' : ''
                            }`}
                            onClick={() => {
                              setSelectedVarName(v.name);
                              setNotice(null);
                              workstationRef.current?.scrollIntoView({ behavior: 'smooth' });
                            }}
                          >
                            {isSelected ? 'Editando ↑' : 'Configurar'}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      <GenerateMatrixModal
        isOpen={isIaModalOpen}
        onClose={() => setIsIaModalOpen(false)}
        projectId={survey.project_id}
        surveyId={survey.id}
        initialQuestion={activeVar?.label || activeVar?.name || ''}
        variableName={activeVar?.name || ''}
        onMatrixGenerated={(genName, genSpec) => {
          if (onOpenMatrixEditorWithSpec) {
            onOpenMatrixEditorWithSpec(genName, genSpec);
          } else if (onOpenMatrixEditor) {
            onOpenMatrixEditor();
          }
        }}
      />
    </div>
  );
}
